import axios from 'axios';
import Notification, { type NotificationContentData } from '@/models/notification';
import { error } from '@/utils/logger';

const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;
const NOTIFICATIONS_ENDPOINT = `${API_URL}/api/v1/notifications/user`;

export interface NotificationTimestamps {
  created: string;
  scheduled?: string;
  sent?: string;
  read?: string;
}

export type NotificationData = NotificationContentData;
export type { NotificationActor } from '@/models/notification';

export interface NotificationContent {
  title: string;
  body: string;
  deepLink?: string;
  imageUrl?: string;
  data: NotificationContentData;
}

export interface GroupedNotifications {
  today: Notification[];
  yesterday: Notification[];
  last7Days: Notification[];
}

export async function fetchNotifications(userId: string): Promise<Notification[]> {
  try {
    const response = await axios.get(`${NOTIFICATIONS_ENDPOINT}/${userId}`, {
      skipAuthErrorHandling: true,
    });
    return Array.isArray(response.data)
      ? response.data.map((item: Record<string, unknown>) => new Notification(item))
      : [];
  } catch (err) {
    error('Error fetching notifications:', err);
    throw err;
  }
}

/**
 * Mark a single notification as read.
 * On 404 (e.g. notification not found or invalid id), returns true so the client can still mark read locally.
 */
export async function markNotificationAsRead(notificationId: string, userId: string): Promise<boolean> {
  try {
    const response = await axios.put(
      `${API_URL}/api/v1/notifications/${notificationId}/read`,
      { userId }
    );
    return response.data;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      return true;
    }
    error('Error marking notification as read:', err);
    throw err;
  }
}

/**
 * Mark multiple notifications as read (e.g. for grouped items using originalNotificationIds).
 * Backend rejects group_ ids; each underlying id must be sent. 404s are treated as success for local state.
 */
export async function markNotificationsAsReadBatch(notificationIds: string[], userId: string): Promise<void> {
  await Promise.all(notificationIds.map((id) => markNotificationAsRead(id, userId)));
}

export function filterLast9Days(notifications: Notification[]): Notification[] {
  const nineDaysAgo = new Date();
  nineDaysAgo.setDate(nineDaysAgo.getDate() - 9);
  nineDaysAgo.setHours(0, 0, 0, 0);

  return notifications.filter(notification => {
    const createdDate = new Date(notification.timestamps.created);
    return createdDate >= nineDaysAgo;
  });
}

export function groupNotificationsByTime(notifications: Notification[]): GroupedNotifications {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const grouped: GroupedNotifications = {
    today: [],
    yesterday: [],
    last7Days: []
  };

  notifications.forEach(notification => {
    const createdDate = new Date(notification.timestamps.created);
    const notifDay = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());

    if (notifDay.getTime() === today.getTime()) {
      grouped.today.push(notification);
    } else if (notifDay.getTime() === yesterday.getTime()) {
      grouped.yesterday.push(notification);
    } else {
      grouped.last7Days.push(notification);
    }
  });

  return grouped;
}
